import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Empty from 'src/components/Empty';
import Button from 'src/components/Button';
import FontAwesomeIcon from 'src/components/Icon';
import Callout from 'src/components/InformationCallout/Callout';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import ModernizedNoteForm from 'src/components/Notes/ModernizedNoteForm';
import NotesRepeater from './NotesRepeater';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import NotesSkeletonList from 'src/components/Notes/skeletons/NotesSkeletonList';
import { filterNotesBySearchTerm } from './helpers';
import { getUrlExtension } from 'src/helpers/CommonHelpers';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import Search from 'src/components/Search/Search';
import {
  UseNotesAPIReturn,
} from 'src/SharedCustomHooks/UseNotesAPI/useNotesAPI';
import { INoteFormConfig } from 'src/SharedCustomHooks/UseNotesAPI/types';
import './ModernizedNotes.scss';

interface Props {
  notesAPI: UseNotesAPIReturn;
  entityId: string;
  itemType: string;
  userId: string;
  canAddNote?: boolean;
  canDelete?: boolean;
  loading?: boolean;
  afterMutate?: () => void;
  addDialogTitle?: string;
  editDialogTitle?: string;
  addDialogDescription?: string;
  editDialogDescription?: string;
  showHomeownerVisibilityCheckbox?: boolean;
  serviceRequestId?: string;
  serviceRequestNumber?: string;
  onNotePinned?: () => void;
}

// TODO: add client-side permissions matrix here (PD peer-visibility, Spark Admin
// global read, role-scoped read vs edit scopes) until the BOS API tech debt is
// addressed — authz should ultimately live server-side on the new endpoints.
const ModernizedNotes: React.FC<Props> = ({
  notesAPI,
  entityId,
  itemType,
  userId,
  canAddNote = true,
  canDelete = false,
  loading = false,
  afterMutate,
  addDialogTitle = 'Add Note',
  editDialogTitle = 'Edit Note',
  addDialogDescription,
  editDialogDescription,
  showHomeownerVisibilityCheckbox = false,
  serviceRequestId,
  serviceRequestNumber,
  onNotePinned,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxNote, setLightboxNote] = useState<any>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<any>(null);

  const reduxUser: any = useSelector((state: any) =>
    selectAuthenticatedUser(state)
  );

  const noteUser: any = reduxUser ?? {
    id: userId,
    roles: [{ role: { name: 'Service Provider' } }],
  };

  const { notes, loadingNotes, saveNotes, updateNote, deleteNote } = notesAPI;

  const editorRole: string = noteUser?.roles?.[0]?.role?.name ?? '';

  const isAdminEditor: boolean =
    editorRole === 'Admin' || editorRole === 'Super Admin';

  const isEditingOthersNote: boolean =
    isAdminEditor &&
    !!editingNote &&
    editingNote.author?.id !== noteUser.id;

  const noteConfig: INoteFormConfig = {
    noteName: 'NOTES',
    noteType: 'NOTES',
    ItemType: itemType,
  };

  const openAdd = (): void => {
    setIsAddOpen(true);
  };

  const closeAdd = (): void => {
    setIsAddOpen(false);
  };

  const openEdit = (note: any): void => {
    setEditingNote(note);
    setIsEditOpen(true);
  };

  const closeEdit = (): void => {
    setIsEditOpen(false);
    setEditingNote(null);
  };

  const handleSave = async (
    title: string,
    body: string,
    visible: boolean,
    image?: File | null
  ): Promise<void> => {
    await saveNotes(
      {
        title,
        body,
        noteVisibleToHomeownerOccupantRealtor: visible,
        image,
      },
      entityId,
      noteConfig
    );

    closeAdd();
    afterMutate?.();
  };

  const handleUpdate = async (
    title: string,
    body: string,
    visible: boolean,
    image?: File | null,
    existingImageUrl?: string
  ): Promise<void> => {
    if (!editingNote?.id) {
      console.warn('[ModernizedNotes] handleUpdate called with no editing note');
      return;
    }

    await updateNote(
      editingNote.id,
      {
        title,
        body,
        noteVisibleToHomeownerOccupantRealtor: visible,
        image,
        existingImageUrl,
      },
      entityId,
      noteConfig
    );

    closeEdit();
    afterMutate?.();
    onNotePinned?.();
  };

  const handleDelete = async (note: any): Promise<void> => {
    await deleteNote(note, entityId);
    afterMutate?.();
  };

  const onImageClicked = (_index: number, note: any): void => {
    if (getUrlExtension(note.image) === 'heic') {
      window.open(note.image, '_blank')?.focus();
      return;
    }

    setLightboxNote(note);
    setLightboxOpen(true);
  };

  const displayNotes = useMemo(
    () => filterNotesBySearchTerm(notes, searchTerm),
    [notes, searchTerm]
  );

  return (
    <div className="offline-sr-notes">
      <div className="offline-sr-notes-header">
        <div className="offline-sr-notes-search">
          <Search
            size="small"
            placeholder="Search notes..."
            value={searchTerm}
            onChange={(e: any) => setSearchTerm(e.target.value)}
            theme={undefined}
          />
        </div>
        {canAddNote && (
          <Button
            color="teal"
            label="Add Note"
            onClick={openAdd}
            icon={<FontAwesomeIcon glyph="edit" weight="light" size="small" />}
            size="small"
          />
        )}
      </div>
      {loading ? (
        <NotesSkeletonList />
      ) : displayNotes.length === 0 ? (
        <Empty
          headline="No notes yet"
          strapline="You can use the Notes section to post any status updates or general comments to keep everyone on the same page."
          image={<img alt="No notes yet" src="/assets/empty/notes.svg" />}
        />
      ) : (
        <div className="offline-sr-notes-scroll">
          <NotesRepeater
            notes={displayNotes}
            user={noteUser}
            enableEdit={true}
            hideDelete={!canDelete}
            onEdit={openEdit}
            onDelete={canDelete ? handleDelete : () => {}}
            onImageClicked={onImageClicked}
            serviceRequestId={serviceRequestId}
            serviceRequestNumber={serviceRequestNumber}
            onNotePinned={onNotePinned}
          />
        </div>
      )}
      <ReusableLightbox
        isOpen={lightboxOpen}
        onClose={() => {
          setLightboxOpen(false);
          setLightboxNote(null);
        }}
        image={lightboxNote?.image}
      />
      <GenericDialogue
        isOpen={isAddOpen}
        onClose={closeAdd}
        title={addDialogTitle}
        description={addDialogDescription}
        maxWidth="100rem"
      >
        {isAddOpen && (
          <ModernizedNoteForm
            key="add-note-form"
            saving={loadingNotes}
            onAddNote={handleSave}
            onClose={closeAdd}
            showHomeownerVisibilityCheckbox={showHomeownerVisibilityCheckbox}
          />
        )}
      </GenericDialogue>
      <GenericDialogue
        isOpen={isEditOpen}
        onClose={closeEdit}
        title={editDialogTitle}
        description={editDialogDescription}
        maxWidth="100rem"
      >
        {isEditOpen && (
          <>
            {isEditingOthersNote && (
              <Callout
                variant="warning"
                title="You're editing another user's note"
                subtitle="As an admin you can edit notes that aren't yours. The original author won't be notified — please be careful and only modify what's necessary."
              />
            )}
            <ModernizedNoteForm
              key={`edit-note-form-${editingNote?.id}`}
              saving={loadingNotes}
              initialNote={editingNote}
              onAddNote={handleSave}
              onEditNote={handleUpdate}
              onClose={closeEdit}
              showHomeownerVisibilityCheckbox={showHomeownerVisibilityCheckbox}
            />
          </>
        )}
      </GenericDialogue>
    </div>
  );
};

export default ModernizedNotes;
